import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, Users, Beer, Trash2 } from "lucide-react";
import { format, parseISO, isSameDay } from "date-fns";
import { toast } from "sonner";

const statusColor = {
  confirmed: "bg-[#3d5a3d] text-white",
  pending: "bg-[#c97b3a] text-white",
  completed: "bg-[#5a5245] text-white",
  cancelled: "bg-[#a34343] text-white",
};

export default function BookingsView({ refreshKey, onChange }) {
  const [bookings, setBookings] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());

  const load = () => api.get("/bookings").then((r) => setBookings(r.data)).catch(() => {});
  useEffect(() => { load(); }, [refreshKey]);

  const bookingDates = useMemo(() => bookings.map((b) => parseISO(b.event_date)), [bookings]);
  const selectedBookings = useMemo(
    () => bookings.filter((b) => isSameDay(parseISO(b.event_date), selectedDate)),
    [bookings, selectedDate]
  );

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}`, { status });
      toast.success("Booking updated");
      load(); onChange?.();
    } catch { toast.error("Update failed"); }
  };

  const removeBooking = async (id) => {
    try {
      await api.delete(`/bookings/${id}`);
      toast.success("Booking deleted");
      load(); onChange?.();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-2 paper-card p-4" data-testid="bookings-calendar">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={(d) => d && setSelectedDate(d)}
          modifiers={{ booked: bookingDates }}
          modifiersClassNames={{ booked: "bg-[#c97b3a]/20 text-[#1f2e1f] font-bold rounded-full" }}
          className="w-full"
        />
        <div className="text-xs text-[#8c8577] mt-3 px-2 flex items-center gap-2">
          <span className="inline-block h-3 w-3 rounded-full bg-[#c97b3a]/40 border border-[#c97b3a]"></span>
          Highlighted = booked event
        </div>
      </div>

      <div className="lg:col-span-3 space-y-4">
        <div>
          <h3 className="font-display text-2xl text-[#1f2e1f]">
            Events on {format(selectedDate, "EEE, MMM d, yyyy")}
          </h3>
          <p className="text-sm text-[#8c8577]">{selectedBookings.length} event(s)</p>
        </div>

        {selectedBookings.length === 0 && (
          <div className="paper-card p-8 text-center text-[#8c8577]">
            No bookings on this day. Pick a highlighted date to see scheduled rentals.
          </div>
        )}

        {selectedBookings.map((b) => (
          <div key={b.id} data-testid={`booking-card-${b.id}`} className="paper-card p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <Badge className={`${statusColor[b.status]} rounded-full font-medium uppercase text-[10px] tracking-wider`}>{b.status}</Badge>
                  <span className="text-xs text-[#8c8577]">{b.event_type}</span>
                </div>
                <h4 className="font-display text-xl text-[#1f2e1f]">{b.customer_name}</h4>
                <p className="text-xs text-[#8c8577]">{b.customer_email}</p>
                <div className="grid sm:grid-cols-2 gap-2 mt-3 text-sm text-[#5a5245]">
                  <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#c97b3a]" />{b.event_location}</div>
                  <div className="flex items-center gap-2"><Users className="h-4 w-4 text-[#c97b3a]" />{b.guest_count} guests</div>
                  <div className="flex items-center gap-2"><Beer className="h-4 w-4 text-[#c97b3a]" />{b.taps_needed} taps · {b.beverage_type}</div>
                  <div className="font-display text-lg text-[#1f2e1f]">${b.price.toLocaleString()}</div>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <Select value={b.status} onValueChange={(v) => updateStatus(b.id, v)}>
                  <SelectTrigger className="w-36 h-9 text-xs" data-testid={`booking-status-${b.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="sm" onClick={() => removeBooking(b.id)} data-testid={`booking-delete-${b.id}`} className="text-[#a34343] hover:bg-[#a34343]/10">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
            {b.notes && <p className="text-sm text-[#5a5245] mt-3 italic border-t border-[#dcd3c6] pt-3">{b.notes}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
